import { MASTER_KALA_PERSONA_ID, type SmartAction } from '@mlm/shared/coaching';
import { BASE_URL, api } from './api';
import { useAuthStore } from './auth-store';

/**
 * Master Kala on the platform's assistant module. The coach app pins the one
 * coaching persona: personas and threads are requested by its id, so the store
 * assistant's personas and chats never appear here, and Kala's chats never
 * appear in the store.
 */

export type KalaPersona = {
  id: string;
  name: string;
  icon: string;
  tagline: string;
  suggestedQuestions: string[];
};

export type KalaConversation = {
  id: string;
  personaId: string;
  title: string | null;
  lastMessageAt: string | null;
  createdAt: string;
};

export type KalaMessage = {
  id: string;
  role: 'user' | 'assistant';
  body: string;
  metadata: {
    refusal?: boolean;
    truncated?: boolean;
    incomplete?: boolean;
    stopped?: boolean;
    smartActions?: SmartAction[];
  };
  createdAt: string;
};

export type KalaSendResult =
  | { ok: true; message: KalaMessage | null; capRemaining: number | null }
  | { ok: false; error: string };

const errorCode = (e: unknown) =>
  (e as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'network_error';

/** Kala's public persona and the member's messages left today (null = not capped). */
export async function getKala(): Promise<{
  persona: KalaPersona | null;
  capRemaining: number | null;
}> {
  const res = await api.get<{ personas: KalaPersona[]; capRemaining: number | null }>(
    '/assistant/personas',
    { params: { personaId: MASTER_KALA_PERSONA_ID } },
  );
  return { persona: res.data.personas[0] ?? null, capRemaining: res.data.capRemaining ?? null };
}

export async function listKalaConversations(): Promise<KalaConversation[]> {
  const res = await api.get<{ conversations: KalaConversation[] }>('/assistant/conversations', {
    params: { personaId: MASTER_KALA_PERSONA_ID, limit: 50 },
  });
  return res.data.conversations;
}

export async function startKalaConversation(): Promise<KalaConversation> {
  const res = await api.post<KalaConversation>('/assistant/conversations', {
    personaId: MASTER_KALA_PERSONA_ID,
  });
  return res.data;
}

/** One page of a thread, newest first (the page reverses for display). */
export async function listKalaMessages(
  conversationId: string,
  cursor?: string,
): Promise<KalaMessage[]> {
  const res = await api.get<{ messages: KalaMessage[] }>(
    `/assistant/conversations/${conversationId}/messages`,
    { params: { limit: KALA_PAGE_SIZE, cursor } },
  );
  return res.data.messages;
}

export const KALA_PAGE_SIZE = 30;

export async function deleteKalaConversation(conversationId: string): Promise<void> {
  await api.delete(`/assistant/conversations/${conversationId}`);
}

async function sendPlain(conversationId: string, body: string): Promise<KalaSendResult> {
  try {
    const res = await api.post<{ message: KalaMessage; capRemaining: number | null }>(
      `/assistant/conversations/${conversationId}/messages`,
      { body },
    );
    return { ok: true, message: res.data.message, capRemaining: res.data.capRemaining };
  } catch (e) {
    return { ok: false, error: errorCode(e) };
  }
}

export type KalaStreamHandlers = {
  onDelta?: (text: string) => void;
  onTool?: (name: string) => void;
};

/**
 * Streamed send over SSE (fetch, because EventSource cannot POST). When the
 * stream never opens (a proxy without streaming, or offline) it falls back
 * once to the plain send; that is safe only because nothing was saved yet.
 * Aborting resolves as `stopped`, and the server keeps what it had written.
 */
export async function sendKalaMessage(
  conversationId: string,
  body: string,
  handlers: KalaStreamHandlers = {},
  signal?: AbortSignal,
): Promise<KalaSendResult> {
  const url = `${BASE_URL}/assistant/conversations/${conversationId}/messages/stream`;
  const request = (token: string | null) =>
    fetch(url, {
      method: 'POST',
      credentials: 'include',
      signal,
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ body }),
    });

  let opened = false;
  try {
    let res = await request(useAuthStore.getState().token);
    if (res.status === 401) {
      const refreshed = await refreshAccessToken();
      if (!refreshed) return { ok: false, error: 'unauthorized' };
      res = await request(refreshed);
    }
    if (!res.ok || !res.headers.get('content-type')?.includes('text/event-stream')) {
      const payload = (await res.json().catch(() => null)) as { error?: string } | null;
      return { ok: false, error: payload?.error ?? 'network_error' };
    }
    if (!res.body) return { ok: false, error: 'network_error' };
    opened = true;

    let result: KalaSendResult | null = null;
    for await (const frame of readSseFrames(res.body)) {
      if (frame.event === 'delta') handlers.onDelta?.((frame.data as { text: string }).text);
      else if (frame.event === 'tool') handlers.onTool?.((frame.data as { name: string }).name);
      else if (frame.event === 'done') {
        const d = frame.data as { message: KalaMessage | null; capRemaining: number | null };
        result = { ok: true, message: d.message, capRemaining: d.capRemaining };
      } else if (frame.event === 'error') {
        result = {
          ok: false,
          error: (frame.data as { error?: string }).error ?? 'assistant_upstream_error',
        };
      }
    }
    return result ?? { ok: false, error: 'stream_incomplete' };
  } catch (e) {
    if (signal?.aborted || (e as Error)?.name === 'AbortError')
      return { ok: false, error: 'stopped' };
    return opened ? { ok: false, error: 'stream_incomplete' } : sendPlain(conversationId, body);
  }
}

async function* readSseFrames(
  stream: ReadableStream<Uint8Array>,
): AsyncGenerator<{ event: string; data: unknown }> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let split = buffer.indexOf('\n\n');
      while (split !== -1) {
        const frame = parseSseChunk(buffer.slice(0, split));
        buffer = buffer.slice(split + 2);
        if (frame) yield frame;
        split = buffer.indexOf('\n\n');
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseSseChunk(chunk: string): { event: string; data: unknown } | null {
  let event = 'message';
  const lines: string[] = [];
  for (const line of chunk.split('\n')) {
    if (line.startsWith(':')) continue;
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) lines.push(line.slice(5).trim());
  }
  if (!lines.length) return null;
  try {
    return { event, data: JSON.parse(lines.join('\n')) };
  } catch {
    return null;
  }
}

async function refreshAccessToken(): Promise<string | null> {
  try {
    const res = await api.post<{ accessToken: string }>('/auth/refresh', {});
    const user = useAuthStore.getState().user;
    if (user) useAuthStore.getState().setSession(res.data.accessToken, user);
    return res.data.accessToken;
  } catch {
    return null;
  }
}
